import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User, fetchUserById, createUser, updateUser } from './UserApi';
import { searchTenantByNames, Tenant } from '../tenant/TenantApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

export type FormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: FormMode;
}

const defaultUser: User = {
  id: '',
  email: '',
  hashedPassword: '',
  mobileNo: '',
  designation: '',
  tenantId: '',
  tenantName: '',
  isActive: true,
  profileImage: '',
  comment: '',
  createdAt: '',
  updatedAt: '',
};

const UserForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState<User>(defaultUser);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantSuggestions, setTenantSuggestions] = useState<Tenant[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState('');

  const isReadOnly = mode === 'view';

  useEffect(() => {
    if ((mode === 'view' || mode === 'edit') && id) {
      loadUserById(id);
    } else if (mode === 'create') {
      resetForm();
    }
  }, [id, mode]);

  const loadUserById = async (userId: string) => {
    setLoading(true);
    try {
      const response = await fetchUserById(userId);
      setUser(response.data);
      setTenantSearch(response.data.tenantName || '');
      setImagePreview(response.data.profileImage || '');
    } catch (err) {
      setError('Failed to fetch user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...user,
        password: password || undefined, // Optional unless required by API
      };
      if (mode === 'edit' && id) {
        await updateUser(id, payload);
      } else if (mode === 'create') {
        await createUser(payload);
      }
      navigate('/admin/users');
    } catch (err) {
      setError('Failed to save user');
    }
  };

  const resetForm = () => {
    setUser(defaultUser);
    setPassword('');
    setTenantSearch('');
    setTenantSuggestions([]);
    setImagePreview(null);
    setImageError('');
  };

  const handleTenantSearch = async (value: string) => {
    setTenantSearch(value);
    if (value.length >= 3) {
      try {
        const response = await searchTenantByNames(value);
        setTenantSuggestions(response.data);
      } catch (err) {
        console.error('Failed to search tenants');
      }
    } else {
      setTenantSuggestions([]);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!validTypes.includes(file.type)) {
      setImageError('Unsupported image format. Allowed: JPEG, PNG, WEBP, GIF.');
      return;
    }

    if (file.size > maxSize) {
      setImageError('Image size must be under 2MB.');
      return;
    }

    setImageError('');

    const reader = new FileReader();
    reader.onloadend = () => {
      const uploadedUrl = `https://yourdomain.com/uploads/${file.name}`;
      setUser((prev) => ({ ...prev, profileImage: uploadedUrl }));
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const renderInput = (label: string, field: keyof User, type = 'text') => (
    <div>
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type={type}
        name={field}
        value={(user[field] as string) || ''}
        onChange={handleChange}
        readOnly={isReadOnly}
        className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
      />
    </div>
  );

  return (
    <div className="p-6 mx-auto bg-white w-full shadow rounded">
      <button
        type="button"
        onClick={() => navigate('/admin/users')}
        className="mb-6 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center gap-2"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        Back
      </button>

      {loading && <div>Loading...</div>}
      {error && <div className="text-red-600">{error}</div>}

      {!loading && (
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderInput('Email', 'email')}
            {renderInput('Mobile No', 'mobileNo')}
            {renderInput('Designation', 'designation')}

            {!isReadOnly && (
              <div>
                <label className="block text-sm font-semibold mb-1">Password</label>
                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required={mode === 'create'}
                  className="border px-3 py-2 rounded w-full"
                />
              </div>
            )}

            {/* Tenant Search */}
            <div>
              <label className="block text-sm font-semibold mb-1">Tenant Name</label>
              <input
                type="text"
                placeholder="Search tenant..."
                value={tenantSearch}
                onChange={(e) => handleTenantSearch(e.target.value)}
                className="border px-3 py-2 rounded w-full"
                disabled={isReadOnly}
              />
              {!isReadOnly && tenantSuggestions.length > 0 && (
                <ul className="border rounded bg-white mt-1 max-h-40 overflow-y-auto">
                  {tenantSuggestions.map((t) => (
                    <li
                      key={t.id}
                      onClick={() => {
                        setUser((prev) => ({ ...prev, tenantId: t.id }));
                        setTenantSearch(t.name);
                        setTenantSuggestions([]);
                      }}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {t.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Status Dropdown */}
            <div>
              <label className="block text-sm font-semibold mb-1">Status</label>
              <select
                name="isActive"
                value={user.isActive ? 'true' : 'false'}
                onChange={(e) =>
                  setUser((prev) => ({ ...prev, isActive: e.target.value === 'true' }))
                }
                disabled={isReadOnly}
                className={`border px-3 py-2 rounded w-full ${isReadOnly ? 'bg-gray-100' : ''}`}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            {renderInput('Comment', 'comment')}

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold mb-1">Profile Image</label>
              {isReadOnly ? (
                user.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt="Profile"
                    className="w-24 h-24 object-cover rounded"
                  />
                ) : (
                  <span>No image</span>
                )
              ) : (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="w-full"
                  />
                  {imageError && (
                    <div className="text-red-600 text-sm mt-1">{imageError}</div>
                  )}
                  {imagePreview && (
                    <div className="mt-2">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-32 h-32 object-cover border rounded"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Uploaded URL:{' '}
                        <a
                          href={user.profileImage}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline"
                        >
                          {user.profileImage}
                        </a>
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            {mode !== 'view' && (
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {mode === 'create' ? 'Create' : 'Update'}
              </button>
            )}
            {mode === 'create' && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Reset
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default UserForm;